import React, { useState, useEffect, useRef } from 'react';
import { UserPlus, Edit, Trash2, Key, Users, LogOut } from 'lucide-react';
import toast from 'react-hot-toast';
import { authAPI, setoresAPI, perfisAcessoAPI } from '../services/api';

const AdminUsuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [setores, setSetores] = useState([]);
  const [sessoes, setSessoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSessoes, setLoadingSessoes] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetingUser, setResetingUser] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    nome_completo: '',
    senha: '',
    perfil: 'assistente',
    setor_id: null,
    setor_ids: [],
    ver_todos_dashboard: false,
    ativo: true,
  });

  const [novaSenha, setNovaSenha] = useState('');

  // RBAC de módulos — estado do modal de edição
  const [modulosAcesso, setModulosAcesso] = useState([]);  // catálogo com flags
  const [modulosExtras, setModulosExtras] = useState(new Set());   // codigos
  const [modulosNegados, setModulosNegados] = useState(new Set()); // codigos
  const [niveisModulos, setNiveisModulos] = useState({});           // codigo -> nivel gravado (ADR 0013)
  // Sugestões de nome vindas da base de beneficiários (só ao criar): quem já
  // está na base entra com o nome padronizado, quem não está é digitado.
  const [sugestoes, setSugestoes] = useState([]);
  const [perfisAcesso, setPerfisAcesso] = useState([]);   // ADR 0013 adendo 1
  const [buscandoNome, setBuscandoNome] = useState(false);
  const sugestaoTimer = useRef(null);
  const buscarSugestoes = (texto) => {
    if (sugestaoTimer.current) clearTimeout(sugestaoTimer.current);
    if (editingUser || !texto || texto.trim().length < 3) { setSugestoes([]); return; }
    sugestaoTimer.current = setTimeout(async () => {
      try {
        setBuscandoNome(true);
        const { data } = await authAPI.sugestoesNome(texto.trim());
        setSugestoes(data?.sugestoes || []);
      } catch (e) { setSugestoes([]); } finally { setBuscandoNome(false); }
    }, 300);
  };
  const [showModulosAvancado, setShowModulosAvancado] = useState(false);

  // Labels amigáveis das seções
  // Toda seção do catálogo (crm.modulos.secao) precisa estar aqui, senão o módulo
  // some desta tela e ninguém consegue liberar/negar por usuário (caso Serviços, 09/2026).
  const NOMES_SECOES = {
    operacional: 'Operacional',
    analises: 'Análises',
    comunicacoes: 'Comunicações',
    administracao: 'Administração',
    servicos: 'Serviços',
    universidade: 'Universidade',
  };

  useEffect(() => {
    perfisAcessoAPI.listar().then(({ data }) => setPerfisAcesso((data.perfis || []).filter(p => p.ativo))).catch(() => setPerfisAcesso([]));
    loadData();
    loadSessoes();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usuariosRes, setoresRes] = await Promise.all([
        authAPI.listUsers(),
        setoresAPI.list(false),
      ]);
      setUsuarios(usuariosRes.data);
      setSetores(setoresRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    }
    setLoading(false);
  };

  const loadSessoes = async () => {
    setLoadingSessoes(true);
    try {
      const res = await authAPI.sessoesAtivas();
      setSessoes(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar sessões:', error);
    }
    setLoadingSessoes(false);
  };

  const handleOpenModal = async (usuario = null) => {
    // Reset do estado de módulos
    setModulosAcesso([]);
    setModulosExtras(new Set());
    setModulosNegados(new Set());
    setShowModulosAvancado(false);

    if (usuario) {
      setEditingUser(usuario);
      setFormData({
        email: usuario.email,
        nome_completo: usuario.nome_completo,
        senha: '',
        perfil: usuario.perfil,
        setor_id: usuario.setor_id,
        setor_ids: usuario.setor_ids || [],
        ver_todos_dashboard: usuario.ver_todos_dashboard || false,
        ativo: usuario.ativo,
      });
      // Carregar catálogo de módulos + overrides já configurados
      try {
        const { data } = await authAPI.getModulosAcesso(usuario.id);
        setModulosAcesso(data.modulos || []);
        setModulosExtras(new Set(
          (data.modulos || []).filter(m => m.override === 'extra').map(m => m.codigo)
        ));
        setModulosNegados(new Set(
          (data.modulos || []).filter(m => m.override === 'negado').map(m => m.codigo)
        ));
        setNiveisModulos(Object.fromEntries(
          (data.modulos || []).filter(m => m.nivel).map(m => [m.codigo, m.nivel])
        ));
        setFormData((f) => ({ ...f, perfil_acesso_id: data.perfil_acesso_id || '' }));
      } catch (err) {
        toast.error('Erro ao carregar permissões de módulos');
      }
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        nome_completo: '',
        senha: '',
        perfil: 'assistente',
        setor_id: null,
        setor_ids: [],
        ver_todos_dashboard: false,
        ativo: true,
      });
      // O acesso se decide NA CRIAÇÃO: o e-mail de boas-vindas sai no salvar e a
      // pessoa já entra vendo o que foi definido, sem uma segunda visita à tela.
      await carregarCatalogoPerfil('assistente');
      setFormData((f) => ({ ...f, perfil_acesso_id: (perfisAcesso.find(p => p.slug === 'assistente') || {}).id || '' }));
    }
    setShowModal(true);
  };

  // Catálogo pelo perfil (sem usuário ainda): padrão do perfil + exceções/níveis zerados
  const carregarCatalogoPerfil = async (perfil) => {
    try {
      const { data } = await authAPI.getModulosAcessoPerfil(perfil);
      setModulosAcesso(data.modulos || []);
      setModulosExtras(new Set());
      setModulosNegados(new Set());
      setNiveisModulos({});
    } catch (err) {
      setModulosAcesso([]);
    }
  };

  // Toggles de módulo (mutualmente exclusivos: extra XOR negado)
  const toggleModuloExtra = (codigo) => {
    const novosExtras = new Set(modulosExtras);
    if (novosExtras.has(codigo)) {
      novosExtras.delete(codigo);
    } else {
      novosExtras.add(codigo);
      // Se estava negado, remove de negados (não pode ser ambos)
      const novosNegados = new Set(modulosNegados);
      novosNegados.delete(codigo);
      setModulosNegados(novosNegados);
    }
    setModulosExtras(novosExtras);
  };

  const toggleModuloNegado = (codigo) => {
    const novosNegados = new Set(modulosNegados);
    if (novosNegados.has(codigo)) {
      novosNegados.delete(codigo);
    } else {
      novosNegados.add(codigo);
      const novosExtras = new Set(modulosExtras);
      novosExtras.delete(codigo);
      setModulosExtras(novosExtras);
    }
    setModulosNegados(novosNegados);
  };

  const handleSave = async () => {
    if (!formData.email || !formData.nome_completo) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      let usuarioId;
      if (editingUser) {
        await authAPI.updateUser(editingUser.id, {
          nome_completo: formData.nome_completo,
          perfil: formData.perfil,
          perfil_acesso_id: formData.perfil_acesso_id ? Number(formData.perfil_acesso_id) : null,
          setor_id: formData.setor_ids.length > 0 ? formData.setor_ids[0] : formData.setor_id,
          setor_ids: formData.setor_ids,
          ver_todos_dashboard: formData.ver_todos_dashboard,
          ativo: formData.ativo,
        });
        usuarioId = editingUser.id;
        toast.success('Usuário atualizado com sucesso!');
      } else {
        const payload = { ...formData, senha: formData.senha || null, perfil_acesso_id: formData.perfil_acesso_id ? Number(formData.perfil_acesso_id) : null };
        const { data: novo } = await authAPI.register(payload);
        usuarioId = novo?.id;
        toast.success('Usuário criado com sucesso!');
      }

      // Salvar overrides de módulos (somente se foi editado/criado com sucesso
      // e há overrides configurados, pra evitar PUT desnecessário)
      if (usuarioId && (modulosExtras.size > 0 || modulosNegados.size > 0 || Object.values(niveisModulos).some(Boolean) || editingUser)) {
        try {
          await authAPI.setModulosOverride(usuarioId, {
            extras: Array.from(modulosExtras),
            negados: Array.from(modulosNegados),
          });
          // níveis por módulo (ADR 0013): manda o estado inteiro dos módulos que declaram níveis
          const comNiveis = modulosAcesso.filter(m => (m.niveis || []).length > 1);
          if (comNiveis.length) {
            await authAPI.setPapeisUsuario(usuarioId, Object.fromEntries(comNiveis.map(m => [m.codigo, niveisModulos[m.codigo] || null])));
          }
        } catch (errOverride) {
          toast.error(
            'Usuário salvo, mas falha ao atualizar permissões de módulos: ' +
            (errOverride.response?.data?.detail || 'erro desconhecido')
          );
        }
      }

      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar usuário');
    }
  };

  const handleDelete = async (usuario) => {
    if (!window.confirm(`Tem certeza que deseja deletar o usuário ${usuario.nome_completo}?`)) {
      return;
    }

    try {
      await authAPI.deleteUser(usuario.id);
      toast.success('Usuário deletado com sucesso!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao deletar usuário');
    }
  };

  const handleOpenResetModal = (usuario) => {
    setResetingUser(usuario);
    setNovaSenha('');
    setShowResetModal(true);
  };

  const handleResetPassword = async () => {
    // Senha opcional: se vazia, sistema gera aleatória e envia email.
    // Se preenchida, exige mínimo 6 caracteres.
    if (novaSenha && novaSenha.length < 6) {
      toast.error('A senha deve ter no mínimo 6 caracteres (ou deixe vazio para o sistema gerar)');
      return;
    }

    try {
      const { data } = await authAPI.resetPassword(resetingUser.id, novaSenha);
      const gerada = data?.senha_gerada_pelo_sistema;
      const enviou = data?.email_enviado;

      if (gerada && enviou) {
        toast.success(`Senha gerada e enviada para ${data.email_destinatario}`);
      } else if (gerada && !enviou) {
        toast.error(`Senha gerada, mas email não enviado: ${data?.erro_email || 'erro desconhecido'}`);
      } else if (enviou) {
        toast.success(`Senha redefinida e email enviado para ${data.email_destinatario}`);
      } else {
        toast.success(`Senha redefinida (email não enviado: ${data?.erro_email || 'erro desconhecido'})`);
      }

      setShowResetModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resetar senha');
    }
  };

  const getPerfilLabel = (perfil) => {
    const perfis = {
      administrador: 'Administrador',
      gerente: 'Gerente',
      coordenador: 'Coordenador',
      analista: 'Analista',
      assistente: 'Assistente',
    };
    return perfis[perfil] || perfil;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div className="search-box" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ color: 'var(--primary)', marginBottom: '0.5rem' }}>
              <Users size={24} style={{ marginRight: '8px', display: 'inline' }} />
              Gerenciar Usuários
            </h2>
            <p style={{ color: 'var(--gray-600)', fontSize: '0.9rem' }}>
              {usuarios.length} usuário(s) cadastrado(s)
            </p>
          </div>
          <button className="btn btn-success" onClick={() => handleOpenModal()}>
            <UserPlus size={18} />
            Novo Usuário
          </button>
        </div>
      </div>

      {/* Tabela de Usuários */}
      <div className="search-box">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--gray-200)' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Nome</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Email</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Perfil</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Setor</th>
              <th style={{ padding: '0.75rem', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '0.75rem', textAlign: 'right' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.map((usuario) => (
              <tr key={usuario.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                <td style={{ padding: '0.75rem' }}>{usuario.nome_completo}</td>
                <td style={{ padding: '0.75rem', color: 'var(--gray-600)' }}>{usuario.email}</td>
                <td style={{ padding: '0.75rem' }}>
                  <span
                    style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      background:
                        usuario.perfil === 'administrador'
                          ? 'var(--danger-light)'
                          : usuario.perfil === 'gerente' || usuario.perfil === 'coordenador'
                          ? 'var(--warning-light)'
                          : 'var(--gray-200)',
                      color:
                        usuario.perfil === 'administrador'
                          ? 'var(--danger)'
                          : usuario.perfil === 'gerente' || usuario.perfil === 'coordenador'
                          ? 'var(--warning)'
                          : 'var(--gray-700)',
                    }}
                  >
                    {getPerfilLabel(usuario.perfil)}
                  </span>
                </td>
                <td style={{ padding: '0.75rem', color: 'var(--gray-600)' }}>
                  {(usuario.setor_ids || []).length > 0
                    ? (usuario.setor_ids || []).map(sid => setores.find(s => s.id === sid)?.nome).filter(Boolean).join(', ') || '-'
                    : usuario.setor?.nome || '-'
                  }
                  {usuario.ver_todos_dashboard && (
                    <span style={{ marginLeft: '6px', fontSize: '0.75rem', background: '#e8f5e9', color: '#2e7d32', padding: '2px 6px', borderRadius: '4px' }}>
                      Dashboard completo
                    </span>
                  )}
                </td>
                <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                  <span
                    className={`status-badge status-${usuario.ativo ? 'ativo' : 'cancelado'}`}
                  >
                    {usuario.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '0.5rem' }}
                      onClick={() => handleOpenResetModal(usuario)}
                      title="Resetar senha"
                    >
                      <Key size={16} />
                    </button>
                    <button
                      className="btn btn-primary"
                      style={{ padding: '0.5rem' }}
                      onClick={() => handleOpenModal(usuario)}
                      title="Editar"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className="btn btn-danger"
                      style={{ padding: '0.5rem' }}
                      onClick={() => handleDelete(usuario)}
                      title="Deletar"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {usuarios.length === 0 && (
          <div className="empty-state" style={{ padding: '2rem' }}>
            <Users size={48} />
            <p>Nenhum usuário cadastrado</p>
          </div>
        )}
      </div>

      {/* Sessões Ativas */}
      <div className="search-box" style={{ marginTop: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <h3 style={{ color: 'var(--primary)', marginBottom: '0.25rem' }}>
              <LogOut size={20} style={{ marginRight: '8px', display: 'inline' }} />
              Sessões Ativas
            </h3>
            <p style={{ color: 'var(--gray-600)', fontSize: '0.9rem' }}>
              {sessoes.length} sessão(ões) ativa(s) nos últimos 30 minutos
            </p>
          </div>
          <button
            className="btn btn-secondary"
            onClick={loadSessoes}
            disabled={loadingSessoes}
            style={{ padding: '0.5rem 1rem' }}
          >
            {loadingSessoes ? 'Atualizando...' : 'Atualizar'}
          </button>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--gray-200)' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Usuário</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Email</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>IP</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Login em</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Último Acesso</th>
            </tr>
          </thead>
          <tbody>
            {sessoes.map((sessao) => (
              <tr key={sessao.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                <td style={{ padding: '0.75rem' }}>
                  {sessao.usuario?.nome_completo || 'Usuário desconhecido'}
                </td>
                <td style={{ padding: '0.75rem', color: 'var(--gray-600)' }}>
                  {sessao.usuario?.email || '-'}
                </td>
                <td style={{ padding: '0.75rem', fontSize: '0.9rem', color: 'var(--gray-600)' }}>
                  {sessao.ip_origem || '-'}
                </td>
                <td style={{ padding: '0.75rem', fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                  {new Date(sessao.login_em).toLocaleString('pt-BR')}
                </td>
                <td style={{ padding: '0.75rem', fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                  {new Date(sessao.ultimo_acesso).toLocaleString('pt-BR')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {sessoes.length === 0 && (
          <div className="empty-state" style={{ padding: '2rem' }}>
            <LogOut size={48} />
            <p>Nenhuma sessão ativa no momento</p>
          </div>
        )}
      </div>

      {/* Modal Criar/Editar Usuário */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingUser ? 'Editar Usuário' : 'Novo Usuário'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                &times;
              </button>
            </div>

            <div className="form-group" style={{ position: 'relative' }}>
              <label>Nome Completo *</label>
              <input
                type="text"
                className="form-control"
                value={formData.nome_completo}
                autoComplete="off"
                placeholder={editingUser ? '' : 'Digite 3 letras: se a pessoa está na base, escolha o nome padronizado'}
                onChange={(e) => { setFormData({ ...formData, nome_completo: e.target.value }); buscarSugestoes(e.target.value); }}
                onBlur={() => setTimeout(() => setSugestoes([]), 150)}
              />
              {!editingUser && (sugestoes.length > 0 || buscandoNome) && (
                <ul style={{ position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 20, margin: 0, padding: '4px 0', listStyle: 'none',
                             background: '#fff', border: '1px solid var(--gray-300)', borderRadius: '6px', boxShadow: '0 6px 18px rgba(0,0,0,0.12)', maxHeight: '220px', overflowY: 'auto' }}>
                  {buscandoNome && sugestoes.length === 0 && <li style={{ padding: '6px 10px', fontSize: '0.8rem', color: 'var(--gray-500)' }}>Buscando na base…</li>}
                  {sugestoes.map((s) => (
                    <li key={`${s.nome}-${s.cpf_mascarado}`} onMouseDown={() => { setFormData((f) => ({ ...f, nome_completo: s.nome })); setSugestoes([]); }}
                        style={{ padding: '6px 10px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: '0.88rem' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gray-100)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                      <span>{s.nome}</span>
                      <span style={{ color: 'var(--gray-500)', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{s.cpf_mascarado}{s.status && s.status !== 'ATIVO' ? ` · ${s.status.toLowerCase()}` : ''}</span>
                    </li>
                  ))}
                  {!buscandoNome && sugestoes.length > 0 && <li style={{ padding: '4px 10px', fontSize: '0.72rem', color: 'var(--gray-500)', borderTop: '1px solid var(--gray-200)' }}>Não está na base? Continue digitando normalmente.</li>}
                </ul>
              )}
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                className="form-control"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={!!editingUser}
              />
            </div>

            {!editingUser && (
              <div className="form-group">
                <label>Senha (Opcional)</label>
                <input
                  type="password"
                  className="form-control"
                  value={formData.senha}
                  onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                  placeholder="Deixe em branco para gerar automaticamente"
                />
                <small style={{ color: 'var(--gray-600)', fontSize: '0.85rem' }}>
                  💡 Se deixar em branco, uma senha aleatória será gerada e enviada por email para {formData.email || 'o usuário'}
                </small>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label>Perfil *</label>
                <select
                  className="form-control"
                  value={formData.perfil}
                  onChange={(e) => { setFormData({ ...formData, perfil: e.target.value }); if (!editingUser) carregarCatalogoPerfil(e.target.value); }}
                >
                  <option value="assistente">Assistente</option>
                  <option value="analista">Analista</option>
                  <option value="coordenador">Coordenador</option>
                  <option value="gerente">Gerente</option>
                  <option value="administrador">Administrador</option>
                </select>
              </div>

              <div className="form-group">
                <label>Perfil de acesso *</label>
                <select
                  className="form-control"
                  value={formData.perfil_acesso_id || ''}
                  onChange={(e) => { const v = e.target.value; setFormData({ ...formData, perfil_acesso_id: v }); if (v) carregarCatalogoPerfil(v); }}
                >
                  <option value="">(de fábrica, pelo perfil acima)</option>
                  {perfisAcesso.map(p => <option key={p.id} value={p.id}>{p.nome}{p.de_fabrica ? ' (fábrica)' : ''} · {p.modulos_liberados} módulo{p.modulos_liberados === 1 ? '' : 's'}</option>)}
                </select>
                <small style={{ color: 'var(--gray-500)' }}>O que a pessoa vê vem daqui. Veja e crie perfis em Administração › Perfis de acesso.</small>
              </div>

            </div>

            <div className="form-group">
              <label>Setores de acesso</label>
              <div style={{
                border: '1px solid var(--gray-300)', borderRadius: '6px', padding: '8px',
                maxHeight: '150px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px'
              }}>
                {setores.map((setor) => (
                  <label key={setor.id} style={{
                    display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px',
                    borderRadius: '4px', cursor: 'pointer',
                    backgroundColor: (formData.setor_ids || []).includes(setor.id) ? '#e8f5e9' : 'transparent'
                  }}>
                    <input
                      type="checkbox"
                      checked={(formData.setor_ids || []).includes(setor.id)}
                      onChange={(e) => {
                        const ids = formData.setor_ids || [];
                        if (e.target.checked) {
                          setFormData({ ...formData, setor_ids: [...ids, setor.id] });
                        } else {
                          setFormData({ ...formData, setor_ids: ids.filter(id => id !== setor.id) });
                        }
                      }}
                    />
                    <span style={{ fontSize: '0.9rem' }}>{setor.nome}</span>
                  </label>
                ))}
                {setores.length === 0 && <span style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>Nenhum setor cadastrado</span>}
              </div>
              <small style={{ color: 'var(--gray-500)', fontSize: '0.8rem' }}>
                Selecione os setores que o usuario pode acessar no Kanban
              </small>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.ver_todos_dashboard}
                  onChange={(e) => setFormData({ ...formData, ver_todos_dashboard: e.target.checked })}
                />
                <span>Visao completa do Dashboard</span>
              </label>
              <small style={{ color: 'var(--gray-500)', fontSize: '0.8rem', marginLeft: '24px', display: 'block' }}>
                Permite ver dados de TODOS os setores no Dashboard (ideal para gerentes e diretores)
              </small>
            </div>

            {editingUser && (
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={formData.ativo}
                    onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                  />
                  Usuario ativo
                </label>
              </div>
            )}

            {/* RBAC de modulos - so disponivel ao editar usuario existente */}
            {modulosAcesso.length > 0 && (
              <div className="form-group" style={{ marginTop: '1rem', borderTop: '1px solid var(--gray-200)', paddingTop: '1rem' }}>
                <button
                  type="button"
                  onClick={() => setShowModulosAvancado(!showModulosAvancado)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary)',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                  }}
                >
                  {showModulosAvancado ? '▼' : '▶'} Permissões de módulos (avançado)
                </button>
                <small style={{ color: 'var(--gray-500)', display: 'block', marginTop: '0.25rem' }}>
                  Por padrão, módulos são liberados pelo perfil "{formData.perfil}".
                  Aqui você define as exceções e o nível em cada módulo{editingUser ? '' : ', já na criação, antes do e-mail de acesso valer'}.
                </small>

                {showModulosAvancado && (
                  <div style={{ marginTop: '1rem', maxHeight: '350px', overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: '6px', padding: '0.75rem' }}>
                    {Object.entries(NOMES_SECOES).map(([secao, nomeSecao]) => {
                      const modulosDaSecao = modulosAcesso.filter(m => m.secao === secao);
                      if (modulosDaSecao.length === 0) return null;
                      return (
                        <div key={secao} style={{ marginBottom: '1rem' }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                            {nomeSecao}
                          </div>
                          {modulosDaSecao.map(m => {
                            const ehPadrao = m.padrao_do_perfil;
                            const ehExtra = modulosExtras.has(m.codigo);
                            const ehNegado = modulosNegados.has(m.codigo);
                            return (
                              <div key={m.codigo} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--gray-100)' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <span style={{ fontSize: '0.9rem' }}>{m.nome}</span>
                                  {(m.niveis || []).length > 1 && ((ehPadrao && !ehNegado) || ehExtra) && (
                                    <select
                                      value={niveisModulos[m.codigo] || m.nivel_base || ''}
                                      onChange={(e) => setNiveisModulos({ ...niveisModulos, [m.codigo]: e.target.value === m.nivel_base ? null : e.target.value })}
                                      title={(m.niveis || []).map(n => `${n.rotulo}: ${n.descricao}`).join('\n')}
                                      style={{ marginLeft: '0.5rem', fontSize: '0.8rem', padding: '2px 6px', borderRadius: '6px', border: '1px solid var(--gray-300)' }}
                                    >
                                      {(m.niveis || []).map(n => <option key={n.nivel} value={n.nivel}>{n.rotulo}</option>)}
                                    </select>
                                  )}
                                  {ehPadrao && (
                                    <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', color: 'var(--gray-500)', backgroundColor: 'var(--gray-100)', padding: '2px 6px', borderRadius: '3px' }}>
                                      Padrão do perfil
                                    </span>
                                  )}
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  {!ehPadrao && (
                                    <label style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                                      <input type="checkbox" checked={ehExtra} onChange={() => toggleModuloExtra(m.codigo)} />
                                      <span style={{ color: '#27ae60' }}>+ Extra</span>
                                    </label>
                                  )}
                                  {ehPadrao && (
                                    <label style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                                      <input type="checkbox" checked={ehNegado} onChange={() => toggleModuloNegado(m.codigo)} />
                                      <span style={{ color: '#e74c3c' }}>− Negar</span>
                                    </label>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                Cancelar
              </button>
              <button className="btn btn-success" onClick={handleSave}>
                {editingUser ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Resetar Senha */}
      {showResetModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Resetar Senha</h3>
              <button className="modal-close" onClick={() => setShowResetModal(false)}>
                &times;
              </button>
            </div>

            <p style={{ marginBottom: '1rem', color: 'var(--gray-600)' }}>
              Resetar senha do usuário <strong>{resetingUser?.nome_completo}</strong>
              {resetingUser?.email && (
                <> (e-mail <strong>{resetingUser.email}</strong>)</>
              )}
            </p>

            <div className="form-group">
              <label>Nova Senha (opcional)</label>
              <input
                type="password"
                className="form-control"
                value={novaSenha}
                onChange={(e) => setNovaSenha(e.target.value)}
                placeholder="Deixe vazio para o sistema gerar"
              />
              <small style={{ color: 'var(--gray-500)', marginTop: '0.25rem', display: 'block' }}>
                {novaSenha
                  ? 'Você definiu uma senha. Será enviada por email ao usuário.'
                  : 'Vazio: sistema gera senha aleatória e envia por email ao usuário.'}
              </small>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowResetModal(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleResetPassword}>
                Resetar Senha
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsuarios;
